/**
 * PayByPhoneSheet — iter122.
 *
 * Type-a-phone-number → settle flow. The natural pattern from UPI/Paytm; users
 * expect it as a first-class option alongside the QR scanner.
 *
 * State machine:
 *   'input'   → phone-input + Contact Picker button + lookup-on-submit
 *   'review'  → confirmation chip (name + initials) + shared-group dropdown
 *               + amount input + auto-fill chips (Full / Half)
 *   'invite'  → user not registered / no shared group → WhatsApp invite CTA
 *   'sending' → POST in flight
 *
 * Auth-aware:
 *   - Privacy-gated lookup (`/users/lookup-by-phone`) — only finds users you
 *     already share a group with.
 *   - If `navigator.contacts.select` is available (Android Chrome PWA), shows
 *     a "Pick from Contacts" shortcut that fills the phone input in one tap.
 */
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Smartphone, Users, Check, ArrowRight, AlertTriangle, Sparkles, Share2, X, BookUser,
} from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import api from '../lib/api';
import { formatNXC } from '../lib/formatNXC';
import UpiPayButton from './UpiPayButton';

const HAS_CONTACT_PICKER = typeof navigator !== 'undefined'
  && 'contacts' in navigator
  && typeof navigator.contacts?.select === 'function';

export default function PayByPhoneSheet({ open, onOpenChange, onSettled, prefill }) {
  const [state, setState] = useState('input'); // input | review | invite | sending
  const [phone, setPhone] = useState('');
  const [looking, setLooking] = useState(false);
  const [match, setMatch] = useState(null);   // found user payload
  const [notFound, setNotFound] = useState(null); // invite payload
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [amountInput, setAmountInput] = useState('');

  const reset = useCallback(() => {
    setState('input'); setPhone(''); setMatch(null); setNotFound(null);
    setSelectedGroupId(''); setAmountInput(''); setLooking(false);
  }, []);

  useEffect(() => {
    if (!open) { reset(); return; }
    // iter125 — direct user_id prefill from a native-camera scan of a personal
    // QR (SettleLandingPage). We already have the user_id and name, so skip
    // the phone lookup entirely and fetch shared groups directly.
    if (prefill?.userIdDirect) {
      (async () => {
        setLooking(true);
        try {
          const [groupsRes, debtsRes] = await Promise.all([
            api.get('/groups'),
            api.get(`/dashboard/debts-with/${prefill.userIdDirect}`).catch(() => ({ data: { groups: [] } })),
          ]);
          const debtMap = {};
          (debtsRes.data?.groups || []).forEach((g) => { debtMap[g.group_id] = g; });
          const shared = (groupsRes.data || [])
            .filter((g) => (g.member_ids || []).includes(prefill.userIdDirect))
            .map((g) => ({ ...g, owe: debtMap[g.id]?.owe || 0, owed: debtMap[g.id]?.owed || 0 }));
          const fake = {
            user: {
              id: prefill.userIdDirect,
              name: prefill.nameDirect || 'Member',
              phone: '',
              upi_id: debtsRes.data?.user?.upi_id || null,
            },
            shared_groups: shared,
          };
          setMatch(fake);
          const top = [...shared].sort((a, b) => (b.owe || 0) - (a.owe || 0))[0];
          if (top) {
            setSelectedGroupId(top.id);
            if (top.owe > 0) setAmountInput(String(top.owe));
          }
          setState('review');
        } catch {
          setState('input');
        } finally { setLooking(false); }
      })();
      return;
    }
    // iter123 — prefill from "Pay again" chips. Skip the input state by
    // running the lookup immediately with the prefilled phone, then if a
    // last_amount/last_group is also passed we auto-snap to them.
    if (prefill?.phone) {
      setPhone(prefill.phone);
      (async () => {
        setLooking(true);
        try {
          const res = await api.get(`/users/lookup-by-phone?phone=${encodeURIComponent(prefill.phone)}`);
          const d = res.data;
          if (d?.found) {
            setMatch(d);
            // Prefer the explicitly provided group; else use the largest-owe.
            const targetGroup = (d.shared_groups || []).find((g) => g.id === prefill.groupId)
              || [...(d.shared_groups || [])].sort((a, b) => (b.owe || 0) - (a.owe || 0))[0];
            if (targetGroup) {
              setSelectedGroupId(targetGroup.id);
              if (prefill.amount && prefill.amount > 0) {
                setAmountInput(String(prefill.amount));
              } else if (targetGroup.owe > 0) {
                setAmountInput(String(targetGroup.owe));
              }
            }
            setState('review');
          } else {
            setNotFound(d);
            setState('invite');
          }
        } catch {
          setState('input');
        } finally { setLooking(false); }
      })();
    }
    // Otherwise the user starts in 'input' state — already reset above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefill]);

  const handleContactPick = async () => {
    if (!HAS_CONTACT_PICKER) {
      toast.info('Contact picker not supported on this browser');
      return;
    }
    try {
      const picked = await navigator.contacts.select(['tel'], { multiple: false });
      const tel = picked?.[0]?.tel?.[0];
      if (tel) setPhone(tel);
    } catch (e) {
      if (e?.name !== 'AbortError') toast.error('Could not access contacts');
    }
  };

  const handleLookup = async () => {
    const trimmed = phone.replace(/\s/g, '');
    if (trimmed.length < 4) {
      toast.error('Enter at least 4 digits');
      return;
    }
    setLooking(true);
    try {
      const res = await api.get(`/users/lookup-by-phone?phone=${encodeURIComponent(trimmed)}`);
      const d = res.data;
      if (d?.found) {
        setMatch(d);
        // Auto-pick the group with the largest "owe" debt
        const groupsByOwe = [...(d.shared_groups || [])].sort((a, b) => (b.owe || 0) - (a.owe || 0));
        if (groupsByOwe.length > 0) {
          setSelectedGroupId(groupsByOwe[0].id);
          if (groupsByOwe[0].owe > 0) setAmountInput(String(groupsByOwe[0].owe));
        }
        setState('review');
      } else if (d?.reason === 'self') {
        toast.error("That's your own number 😉");
      } else {
        // not_registered or no_shared_group → show invite
        setNotFound(d);
        setState('invite');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Lookup failed');
    } finally { setLooking(false); }
  };

  const handleConfirm = async () => {
    const amt = Number(amountInput);
    if (!amt || amt <= 0) { toast.error('Enter a positive amount'); return; }
    if (!selectedGroupId) { toast.error('Pick a group to settle in'); return; }
    if (!match?.user?.id) return;
    setState('sending');
    try {
      const res = await api.post('/coin-transfers/qr-settle', {
        group_id: selectedGroupId,
        to_user_id: match.user.id,
        amount: amt,
        note: `Phone settle · ${phone}`,
      });
      toast.success(res.data?.message || `Settled ${formatNXC(amt)} with ${match.user.name}`);
      onSettled?.(res.data?.settlement);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to settle');
      setState('review');
    }
  };

  /**
   * iter124 — Settle-All-with-X across every group where I owe this person.
   * Sends one POST containing all owe>0 groups. Returns per-group breakdown
   * so we can show a celebratory summary toast.
   */
  const handleSettleAll = async () => {
    if (!match?.user?.id) return;
    const owedGroups = (match.shared_groups || []).filter((g) => (g.owe || 0) > 0);
    if (owedGroups.length === 0) { toast.error('No outstanding owes to settle'); return; }
    setState('sending');
    try {
      const res = await api.post('/coin-transfers/qr-settle-bulk', {
        to_user_id: match.user.id,
        settlements: owedGroups.map((g) => ({
          group_id: g.id,
          amount: g.owe,
          note: `Bulk phone settle · ${phone}`,
        })),
      });
      toast.success(res.data?.message || `Settled with ${match.user.name}`);
      if ((res.data?.failed || []).length > 0) {
        toast.warning(`${res.data.failed.length} leg(s) failed — open the group(s) to retry`);
      }
      onSettled?.(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk settle failed');
      setState('review');
    }
  };

  const initials = (n) => (n || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-3xl" data-testid="pay-by-phone-sheet">
        <SheetHeader>
          <SheetTitle className="font-display text-xl inline-flex items-center gap-2">
            <Smartphone className="w-5 h-5" />
            Settle by number
          </SheetTitle>
          <SheetDescription>
            Type your friend's phone number to settle without scanning a QR.
            This is between the two of you — <span className="font-semibold">the group fund is never touched</span>.
          </SheetDescription>
        </SheetHeader>

        <div className="py-4 min-h-[320px]">
          {/* ---------- INPUT ---------- */}
          {state === 'input' && (
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-1.5 block">
                  Phone number
                </label>
                <Input
                  type="tel"
                  inputMode="tel"
                  autoFocus
                  placeholder="98200 30046"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
                  className="text-lg font-medium tabular-nums h-12"
                  data-testid="pay-phone-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Only finds people you already share a group with — that keeps your contact list private.
                </p>
              </div>

              {HAS_CONTACT_PICKER && (
                <Button
                  variant="outline"
                  className="w-full rounded-full text-xs"
                  onClick={handleContactPick}
                  data-testid="pay-phone-pick-contact"
                >
                  <BookUser className="w-3.5 h-3.5 mr-1" />
                  Pick from Contacts
                </Button>
              )}

              <Button
                className="w-full rounded-full"
                onClick={handleLookup}
                disabled={looking || phone.replace(/\s/g, '').length < 4}
                data-testid="pay-phone-lookup"
              >
                {looking ? (
                  <>
                    <Sparkles className="w-4 h-4 mr-1 animate-pulse" /> Searching…
                  </>
                ) : (
                  <>
                    Find friend <ArrowRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          )}

          {/* ---------- REVIEW ---------- */}
          {state === 'review' && match?.user && (
            <div className="space-y-4" data-testid="pay-phone-review">
              <Card className="p-3 rounded-2xl border-2 border-emerald-500/40 bg-emerald-50/40 dark:bg-emerald-950/15 flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center font-display font-bold text-lg shrink-0">
                  {initials(match.user.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-display text-xl font-bold truncate">{match.user.name}</p>
                  <p className="text-xs text-muted-foreground tabular-nums">{match.user.phone}</p>
                </div>
                <button
                  className="p-2 text-muted-foreground hover:text-foreground"
                  onClick={reset}
                  data-testid="pay-phone-clear"
                  aria-label="Search again"
                >
                  <X className="w-4 h-4" />
                </button>
              </Card>

              {/* iter124 — Settle All with X (hero) when owed across multiple groups */}
              {(() => {
                const owedGroups = (match.shared_groups || []).filter((g) => (g.owe || 0) > 0);
                if (owedGroups.length < 2) return null;
                const total = owedGroups.reduce((s, g) => s + g.owe, 0);
                return (
                  <Card className="p-4 rounded-2xl border-2 border-primary/50 bg-primary/5 space-y-2" data-testid="settle-all-card">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="min-w-0">
                        <p className="text-xs uppercase tracking-wider text-primary font-bold">
                          Across {owedGroups.length} groups
                        </p>
                        <p className="font-display text-2xl font-bold tabular-nums mt-0.5">
                          {formatNXC(total)}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        className="rounded-full"
                        onClick={handleSettleAll}
                        data-testid="settle-all-btn"
                      >
                        <Check className="w-3.5 h-3.5 mr-1" /> Settle ALL
                      </Button>
                    </div>
                    <div className="space-y-1 pt-1 border-t border-primary/20">
                      {owedGroups.map((g) => (
                        <div key={g.id} className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground truncate">{g.name}</span>
                          <span className="font-semibold tabular-nums text-rose-600 dark:text-rose-300">
                            {formatNXC(g.owe)}
                          </span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground italic">
                      Each group gets its own settlement row — audit stays clean. Or pick a single group below.
                    </p>
                  </Card>
                );
              })()}

              {/* group picker */}
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-1.5 block inline-flex items-center gap-1">
                  <Users className="w-3 h-3" /> Which group?
                </label>
                <Select value={selectedGroupId} onValueChange={(v) => {
                  setSelectedGroupId(v);
                  const g = match.shared_groups?.find((x) => x.id === v);
                  if (g?.owe > 0) setAmountInput(String(g.owe));
                }}>
                  <SelectTrigger className="h-11 rounded-xl" data-testid="pay-phone-group-select">
                    <SelectValue placeholder="Pick a shared group" />
                  </SelectTrigger>
                  <SelectContent>
                    {match.shared_groups?.map((g) => (
                      <SelectItem key={g.id} value={g.id}>
                        {g.name}
                        {g.owe > 0 && (
                          <span className="ml-2 text-xs text-rose-600 dark:text-rose-300 font-bold">
                            · owe {formatNXC(g.owe)}
                          </span>
                        )}
                        {g.owed > 0 && (
                          <span className="ml-2 text-xs text-emerald-600 dark:text-emerald-300 font-bold">
                            · owed {formatNXC(g.owed)}
                          </span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* auto-fill chips */}
                {selectedGroupId && (() => {
                  const g = match.shared_groups?.find((x) => x.id === selectedGroupId);
                  const owe = g?.owe || 0;
                  if (owe <= 0) return null;
                  return (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="pay-phone-debt-chips">
                      <button
                        type="button"
                        onClick={() => setAmountInput(String(owe))}
                        className="text-xs px-2.5 py-1 rounded-full bg-rose-500/15 text-rose-700 dark:text-rose-300 font-bold hover:bg-rose-500/25 transition-colors"
                      >
                        Full owed · {formatNXC(owe)}
                      </button>
                      {owe >= 200 && (
                        <button
                          type="button"
                          onClick={() => setAmountInput(String(Math.round(owe / 2)))}
                          className="text-xs px-2.5 py-1 rounded-full bg-muted text-foreground/80 font-semibold hover:bg-muted/70 transition-colors"
                        >
                          Half · {formatNXC(Math.round(owe / 2))}
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* amount input */}
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-1.5 block">
                  Amount (₹)
                </label>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  inputMode="numeric"
                  value={amountInput}
                  onChange={(e) => setAmountInput(e.target.value)}
                  placeholder="0"
                  className="text-2xl font-display font-bold tabular-nums h-14 text-center"
                  data-testid="pay-phone-amount-input"
                />
              </div>

              <Button
                className="w-full rounded-full"
                onClick={handleConfirm}
                disabled={!amountInput || Number(amountInput) <= 0 || !selectedGroupId}
                data-testid="pay-phone-confirm"
              >
                <Check className="w-4 h-4 mr-1" /> Paid in cash — record {amountInput ? formatNXC(Number(amountInput)) : ''}
              </Button>

              {/* iter235 — real-money UPI deep-link when payee saved a UPI ID */}
              {match.user.upi_id && Number(amountInput) > 0 && selectedGroupId && (
                <>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-px bg-border" />
                    <span className="text-xs uppercase tracking-wider text-muted-foreground font-bold">or</span>
                    <div className="flex-1 h-px bg-border" />
                  </div>
                  <UpiPayButton
                    upiId={match.user.upi_id}
                    payeeName={match.user.name}
                    amount={Number(amountInput)}
                    note={`SplitEasy: ${match.shared_groups?.find((g) => g.id === selectedGroupId)?.name || 'settle'}`}
                    onConfirmed={handleConfirm}
                  />
                </>
              )}
            </div>
          )}

          {/* ---------- INVITE (not found / no shared group) ---------- */}
          {state === 'invite' && notFound && (
            <div className="space-y-4 text-center" data-testid="pay-phone-invite">
              <div className="flex flex-col items-center gap-2 pt-4">
                <div className="w-14 h-14 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-7 h-7 text-amber-500" />
                </div>
                <p className="font-display text-lg font-bold">
                  {notFound.reason === 'not_registered' ? 'Not on SplitEasy yet' : 'No shared group'}
                </p>
                <p className="text-sm text-muted-foreground max-w-xs">
                  {notFound.reason === 'not_registered'
                    ? `${phone} isn't using SplitEasy. Send them a WhatsApp invite — once they join, you can settle in seconds.`
                    : `That person uses SplitEasy but you don't share a group yet. Send them an invite to join one of yours.`}
                </p>
              </div>
              <Button
                className="w-full rounded-full"
                onClick={() => { window.open(notFound.invite_url, '_blank'); }}
                data-testid="pay-phone-invite-cta"
              >
                <Share2 className="w-4 h-4 mr-1.5" />
                Invite via WhatsApp
              </Button>
              <Button
                variant="outline"
                className="w-full rounded-full"
                onClick={reset}
                data-testid="pay-phone-try-again"
              >
                Try another number
              </Button>
            </div>
          )}

          {/* ---------- SENDING ---------- */}
          {state === 'sending' && (
            <div className="flex flex-col items-center justify-center min-h-[260px] gap-3" data-testid="pay-phone-sending">
              <div className="w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin" />
              <p className="text-sm text-muted-foreground">Updating ledgers…</p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
